/**
 * Threaded comments on a feedback ticket. Used by both the admin page and the
 * submitter ("My Feedback") page — same component, the `isAdmin` flag only
 * routes the underlying API calls to the right endpoint. Comments are
 * immutable: no edit, no delete. When the ticket is locked (Confirmed Fixed
 * or Deferred) the composer is hidden.
 */
import { useCallback, useState } from 'react';
import {
  Avatar, Box, Button, Chip, Paper, TextField, Typography,
} from '@mui/material';
import { ChatBubbleOutline, Send } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import { addComment, addMyComment } from '../../services/feedbackService';
import type { FeedbackComment } from '../../types/feedback';

interface Props {
  ticketKey: string;
  comments: FeedbackComment[];
  /** When true, all comment calls go through the admin route (`/feedback/:id/comments`). */
  isAdmin: boolean;
  /** Hides the composer (e.g. ticket is in a locked status). */
  locked: boolean;
  /** Called after a successful post so the parent can refresh the ticket. */
  onPosted: () => void;
}

export const CommentsThread = ({ ticketKey, comments, isAdmin, locked, onPosted }: Props) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');

  const handlePost = useCallback(async () => {
    const body = draft.trim();
    if (!body) return;
    setPosting(true);
    setError('');
    try {
      if (isAdmin) await addComment(ticketKey, body);
      else await addMyComment(ticketKey, body);
      setDraft('');
      onPosted();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to post comment');
    } finally {
      setPosting(false);
    }
  }, [draft, isAdmin, ticketKey, onPosted]);

  return (
    <Paper variant="outlined" sx={{
      p: 2, mb: 2.5, borderRadius: 1.5,
      // Olive left rail + cream-tinted surface so the panel sits in the same
      // family as Edit Report / Proposed Solution / Submitter Actions instead
      // of reading as a stock MUI white card.
      borderLeft: `4px solid ${primary}`,
      bgcolor: alpha(primary, 0.025),
      borderColor: alpha(primary, 0.2),
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
        <ChatBubbleOutline sx={{ fontSize: 17, color: primary }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'primary.main' }}>
          Comments
        </Typography>
        {comments.length > 0 && (
          <Chip label={comments.length} size="small" sx={{
            height: 18, fontSize: '0.65rem', fontWeight: 700,
            bgcolor: alpha(primary, 0.12), color: primary,
            border: `1px solid ${alpha(primary, 0.3)}`,
          }} />
        )}
      </Box>

      {comments.length === 0 ? (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: locked ? 0 : 1.5, fontStyle: 'italic' }}>
          No comments yet.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: locked ? 0 : 2 }}>
          {comments.map((c) => <CommentRow key={c.FeedbackCommentKey} comment={c} />)}
        </Box>
      )}

      {!locked && (
        <Box>
          <TextField
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={isAdmin ? 'Reply to the submitter…' : 'Add a comment…'}
            fullWidth multiline minRows={2} maxRows={8} size="small"
            inputProps={{ maxLength: 10_000 }}
            sx={{
              mb: 1,
              // Match the cream/olive theme — composer is part of the panel,
              // not a foreign MUI input. Background stays paper-white when
              // focused for readability.
              '& .MuiOutlinedInput-root': {
                bgcolor: '#FEFCF5',
                '& fieldset': { borderColor: alpha(primary, 0.25) },
                '&:hover fieldset': { borderColor: alpha(primary, 0.5) },
                '&.Mui-focused fieldset': { borderColor: primary, borderWidth: 1.5 },
              },
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {error && <Typography variant="caption" color="error">{error}</Typography>}
            <Box sx={{ flex: 1 }} />
            <Button
              size="small" variant="contained" color="primary"
              startIcon={<Send sx={{ fontSize: 16 }} />}
              disabled={posting || !draft.trim()} onClick={handlePost}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              {posting ? 'Posting…' : 'Post comment'}
            </Button>
          </Box>
        </Box>
      )}

      {locked && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
          This ticket is locked. New comments are disabled.
        </Typography>
      )}

      {!locked && comments.length > 0 && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75, fontSize: '0.65rem' }}>
          Comments cannot be edited once posted — add a new one to clarify.
        </Typography>
      )}
    </Paper>
  );
};

interface RowProps { comment: FeedbackComment }

const CommentRow = ({ comment }: RowProps) => {
  const theme = useTheme();
  const isSubmitter = comment.AuthorRole === 'submitter';
  // Submitters carry the olive primary (the "house" voice of the lab);
  // admins carry the muted-brown secondary. Both colors are already in the
  // app's palette so the rows feel native to MoftakiLab, not generic chat.
  const tint = isSubmitter ? theme.palette.primary.main : theme.palette.secondary.main;
  const displayName = comment.AuthorName || comment.AuthorUserId;
  const initial = (displayName || '?').trim().charAt(0).toUpperCase();

  return (
    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
      <Avatar sx={{
        width: 30, height: 30, fontSize: '0.78rem', fontWeight: 700,
        bgcolor: alpha(tint, 0.16),
        color: tint,
        border: `1px solid ${alpha(tint, 0.35)}`,
      }}>
        {initial}
      </Avatar>
      <Box sx={{
        flex: 1, p: 1.25, pl: 1.5,
        borderRadius: 1.25,
        // Cream paper surface like the notebook cards in card-view, with
        // a thin role-tinted left rail rather than a fully-tinted card so
        // the comments read as discrete notebook entries.
        bgcolor: '#FEFCF5',
        border: `1px solid ${alpha(tint, 0.22)}`,
        borderLeft: `3px solid ${tint}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{
            fontWeight: 700, fontSize: '0.78rem', color: tint,
            fontFamily: 'Georgia, "Times New Roman", serif',
            letterSpacing: '0.005em',
          }}>
            {displayName}
          </Typography>
          <Chip
            label={isSubmitter ? 'Submitter' : 'Admin'}
            size="small"
            sx={{
              height: 16, fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.06em',
              textTransform: 'uppercase',
              bgcolor: alpha(tint, 0.12), color: tint,
              border: `1px solid ${alpha(tint, 0.3)}`,
            }}
          />
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" sx={{
            fontSize: '0.66rem',
            fontFamily: '"Roboto Mono", Consolas, monospace',
            color: alpha('#6b6555', 0.85),
            letterSpacing: '0.01em',
          }}>
            {new Date(comment.sysCreateDate).toLocaleString()}
          </Typography>
        </Box>
        {/* Comment bodies are markdown (agent comments use **bold**, lists,
            links). Rendered with the same conventions as ProposedSolutionCard:
            compact typography, links open in a new tab. Plain-text comments
            pass through unchanged (markdown is a superset for simple prose). */}
        <Box sx={{
          lineHeight: 1.6, fontSize: '0.87rem', color: '#2d3628',
          '& p': { my: 0.5 },
          '& p:first-of-type': { mt: 0 },
          '& p:last-of-type': { mb: 0 },
          '& ul, & ol': { my: 0.5, pl: 2.5 },
          '& li': { mb: 0.25 },
          '& code': {
            bgcolor: alpha(tint, 0.08), px: 0.5, py: 0.1, borderRadius: 0.5,
            fontSize: '0.8em', fontFamily: 'monospace',
          },
          '& pre': { bgcolor: alpha(tint, 0.06), p: 1.25, borderRadius: 1, overflow: 'auto', fontSize: '0.8rem' },
          '& pre code': { bgcolor: 'transparent', p: 0 },
          '& blockquote': { borderLeft: `3px solid ${alpha(tint, 0.4)}`, pl: 1.5, my: 0.75, color: 'text.secondary' },
          '& a': { color: tint },
          '& h1, & h2, & h3, & h4': { mt: 1, mb: 0.5, fontWeight: 600, fontSize: '0.92rem' },
        }}>
          <ReactMarkdown
            components={{
              // Links open in a new tab so the reader stays on the ticket page.
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer" />
              ),
            }}
          >
            {comment.Body}
          </ReactMarkdown>
        </Box>
      </Box>
    </Box>
  );
};
